import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PlotlyModule } from 'angular-plotly.js';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { submitSearch } from '../../../../state/action';
import { IdeaGenStateService } from '../../idea-gen-state.service';
import { IdeaGenerationServiceService } from '../../services/idea-generation-service.service';
import {
  IIdeaGenCluster,
  IIdeaGenQuery,
  IIdeaGenSnapshot,
} from '../../../../core/models/idea-generation.model';
import { NullableNumberPipe } from '../../../../shared/pipes/nullable-number.pipe';
import {
  STAGE_COLORS,
  STAGE_BG_COLORS,
  getStageColor,
  getStageBgColor,
} from '../../../../shared/utils/bracket.utils';

@Component({
  selector: 'app-idea-snapshot',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, PlotlyModule, NullableNumberPipe],
  templateUrl: './idea-snapshot.component.html',
  styleUrl: './idea-snapshot.component.css',
})
export class IdeaSnapshotComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  showSpinner = false;
  showContent = false;
  showDataMissing = false;
  errorMessage = '';

  snapshot: IIdeaGenSnapshot | null = null;

  topExpanded: number | null = null;
  flopExpanded: number | null = null;

  treemapFigure: { data: any[]; layout: any; config: any } | null = null;

  // drill-down selection (driven by plotly click)
  selectedCluster: { cluster_id: number; cluster_name: string; stage: string; relative_growth: number; invention_count_12m: number } | null = null;

  constructor(
    private store: Store,
    private stateService: IdeaGenStateService,
    private ideaGenService: IdeaGenerationServiceService,
  ) {}

  ngOnInit(): void {
    this.stateService.query$.pipe(takeUntil(this.destroy$)).subscribe((query) => {
      this.loadSnapshot(query);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSnapshot(query: IIdeaGenQuery): void {
    this.showSpinner = true;
    this.showContent = false;
    this.showDataMissing = false;
    this.errorMessage = '';
    this.topExpanded = null;
    this.flopExpanded = null;
    this.selectedCluster = null;
    this.stateService.incrementActiveRequests();

    this.ideaGenService.getSnapshot(query).subscribe({
      next: (res) => {
        this.stateService.decrementActiveRequests();
        if (res.status === 203 || !res.body?.payload) {
          this.showSpinner = false;
          this.showDataMissing = true;
          return;
        }
        this.snapshot = res.body.payload;
        this.buildTreemap();
        this.showSpinner = false;
        this.showContent = true;
      },
      error: () => {
        this.stateService.decrementActiveRequests();
        this.showSpinner = false;
        this.errorMessage = 'Failed to load idea generation snapshot.';
      },
    });
  }

  private buildTreemap(): void {
    if (!this.snapshot) return;
    const tm = this.snapshot.treemap;

    const cmin = -100;
    const cmax = 100;

    const trace: any = {
      type: 'treemap',
      branchvalues: 'total',
      ids: tm.ids,
      labels: tm.labels,
      parents: tm.parents,
      values: tm.values,
      marker: {
        colors: tm.relative_growth,
        colorscale: [
          [0, '#d60000'],
          [(0 - cmin) / (cmax - cmin), 'white'],
          [(25 - cmin) / (cmax - cmin), 'green'],
          [1, 'green'],
        ],
        cmin,
        cmax,
        colorbar: {
          title: 'Relative Growth (%)',
          tickvals: [cmin, 0, cmax],
          ticktext: [`${cmin}%`, '0%', `>${cmax}%`],
        },
      },
      text: tm.labels.map((label, i) => {
        if (i === 0) return '';
        const rg = tm.relative_growth[i];
        const abs = tm.values[i];
        const stage = tm.stage[i] || 'No Stage';
        const fmt = rg != null && !isNaN(rg) ? rg.toFixed(2) : 'N/A';
        return `Stage: ${stage}<br>Relative Growth: ${fmt}%<br>Absolute Growth: ${abs} Inv`;
      }),
    };

    this.treemapFigure = {
      data: [trace],
      layout: {
        title: 'Innovation Landscape',
        margin: { t: 50, l: 25, r: 25, b: 25 },
        height: 800,
      },
      config: {
        responsive: true,
        displaylogo: false,
        toImageButtonOptions: {
          format: 'png',
          filename: 'innovation_treemap',
          height: 900,
          width: 1600,
          scale: 2,
        },
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
      },
    };
  }

  onTreemapClick(event: any): void {
    const clicked = event?.points?.[0];
    if (!clicked) return;
    // match old theme-trends logic: only set selection when a child node is clicked
    if (clicked.parent && clicked.parent !== '' && clicked.label !== 'All') {
      const cid = Number(clicked.id);
      const tm = this.snapshot?.treemap;
      const idx = tm?.ids.indexOf(clicked.id);
      const stage = idx != null && idx >= 0 ? (tm?.stage[idx] ?? '') : '';
      const rg = idx != null && idx >= 0 ? (tm?.relative_growth[idx] ?? 0) : 0;
      const inv = idx != null && idx >= 0 ? (tm?.values[idx] ?? 0) : (clicked.value ?? 0);
      this.selectedCluster = {
        cluster_id: cid,
        cluster_name: clicked.label,
        stage,
        relative_growth: rg,
        invention_count_12m: inv,
      };
    } else {
      this.selectedCluster = null;
    }
  }

  exploreSelected(): void {
    if (!this.selectedCluster) return;
    this.submitCluster(this.selectedCluster.cluster_id, this.selectedCluster.cluster_name);
  }

  clearSelected(): void {
    this.selectedCluster = null;
  }

  toggleTopExpand(i: number): void {
    this.topExpanded = this.topExpanded === i ? null : i;
  }

  toggleFlopExpand(i: number): void {
    this.flopExpanded = this.flopExpanded === i ? null : i;
  }

  submitCluster(clusterId: number, clusterName: string): void {
    const searchParams = {
      themes: [{
        theme_search_type: 'cluster',
        cluster_id: String(clusterId),
        cluster_name: clusterName,
        api_query: null,
        name: clusterName,
      }],
    };
    this.store.dispatch(submitSearch({ searchParams, searchType: 'theme' }));
  }

  trackCluster(_i: number, c: IIdeaGenCluster): number {
    return c.cluster_id;
  }

  stageLabel(stage: string): string {
    return stage && stage.trim().length > 0 ? stage : 'No Stage';
  }

  stageColor(stage: string): string {
    return getStageColor(this.stageLabel(stage));
  }

  stageBgColor(stage: string): string {
    return getStageBgColor(this.stageLabel(stage));
  }
}
